import { Injectable, Signal, computed, inject, isSignal, signal } from '@angular/core';
import { ICalScanCloudParams, ICalScanError, TCalConsumerError } from '@calsale/scanovate';
import {
  IHttpStatus,
  IScanDocumentData,
  IScanDocumentModel,
  IScanValidateData,
  IScanValidateModel
} from '@partners/api';
import { IOcrScanDocsOption, LocalCatalog } from '@partners/catalog';
import { IRemoteError } from '@partners/errors';
import { HttpService, filterStatus, tapResult } from '@partners/http';
import { SettingsMap } from '@partners/settings';
import { ScanDocumentStore } from '@partners/state';
import { isFunction, takeOnce } from '@partners/utils';
import { Observable, catchError, of } from 'rxjs';
import { ContextBuilder } from '../../../http/services/context-builder.service';
import { NavigationAdapter } from '../../../navigation';
import { ScanSeverities, TScanProcessError } from '../errors';
@Injectable({ providedIn: 'root' })
export class ScanFacade {
  genderName$$: Signal<string>;
  #scanSeverities = inject(ScanSeverities);
  #httpService = inject(HttpService);
  #scanDocumentStore = inject(ScanDocumentStore);
  #navAdapter = inject(NavigationAdapter);

  readonly documentToScanOptions$$ = signal<IOcrScanDocsOption[]>([]);
  readonly selectedDocToScan$$: Signal<number>;

  isOcrFailed$$ = computed(() => this.#scanDocumentStore.error$$());

  constructor() {
    this.selectedDocToScan$$ = computed(() => this.#scanDocumentStore.model$$()?.selectedDocId);
    this.genderName$$ = computed(() => {
      return this.#getGenderNameByCode();
    });
  }

  updateDocNameForDisplay(documentToScanOptions: IOcrScanDocsOption[]): IOcrScanDocsOption[] {
    return documentToScanOptions?.map(({ key, value }) => {
      return { key, value };
    });
  }

  /**
   * @description in here we will handle errors from the scan process itself.
   * first we will  take the error code thrown to us by the scan iframe.
   * than extract how to handle it from scanErrorHandlers.
   * if the error code is unknown we will extract from scanErrorHandlers by code equals to 0
   * @param error
   */
  handleScanError({ code, name }: ICalScanError): void {
    const knownErrorCodeHandler = this.#scanSeverities.scanErrorHandlers.get(code as TScanProcessError);
    const knownErrorNameHandler = this.#scanSeverities.scanErrorHandlers.get(name as TCalConsumerError);
    if (isFunction(knownErrorCodeHandler)) {
      knownErrorCodeHandler.call(this);
      return;
    }
    if (isFunction(knownErrorNameHandler)) {
      knownErrorNameHandler.call(this);
      return;
    }
    const unknownErrorHandler = this.#scanSeverities.scanErrorHandlers.get('unknown');
    if (isFunction(unknownErrorHandler)) {
      unknownErrorHandler.call(this);
      return;
    }
  }

  handleScanCompletion(params: ICalScanCloudParams | Error, selectedDocId: number): Observable<IHttpStatus> {
    return this.#getScanResult(params as ICalScanCloudParams, selectedDocId);
  }

  updateGender(gender: number): void {
    this.#scanDocumentStore.dispatchSubmitScanDocument({ gender });
    const { name } = SettingsMap.scanDetails();
    this.#navAdapter.moveTo(name);
  }

  isMaxAttemptsExceeded$$(): Signal<boolean> {
    return computed(() => {
      return this.#scanDocumentStore.data$$()?.isMaxAttemptsExceeded;
    });
  }

  #getGenderNameByCode(): any {
    const data$$ = this.#scanDocumentStore.data$$;
    const model$$ = this.#scanDocumentStore.model$$;
    if (!isSignal(data$$) || !isSignal(model$$)) {
      return '';
    }
    const genderValue = data$$()?.gender || model$$()?.gender;
    const name = LocalCatalog.genderList$$()?.find((item) => item.value === +genderValue)?.key;
    return name;
  }

  #getScanResult(params: ICalScanCloudParams, selectedDocId: number): Observable<IHttpStatus> {
    const model: IScanDocumentModel = { sessionToken: params?.token, processId: params?.processId };

    this.#scanDocumentStore.dispatchSubmitScanDocument({ ...model, selectedDocId });
    return this.#httpService.post<IScanDocumentModel, IScanDocumentData>('scan-result', model).pipe(
      catchError((error: IRemoteError) => {
        this.#scanDocumentStore.dispatchSubmitScanDocumentFailure({ error });
        return this.#scanSeverities.inspect(error);
      }),
      tapResult((data: IScanDocumentData) => {
        this.#scanDocumentStore.dispatchUpdateLastScanAction('scan');
        this.#scanDocumentStore.dispatchSubmitScanDocumentSuccess({ data });
      }),
      filterStatus()
    );
  }

  /**
   * @description Check if the scan details match the details the user already provided.
   * If they do continue else navigate to the scan-validate-alert page and display the mismatch details.
   */
  validateScannedDocument(): Observable<IHttpStatus> {
    const options = [...SettingsMap.scanDocument().loaderOptions.send];
    const context = new ContextBuilder().withLoaderAnimation(options).build();
    return this.#httpService.fetch<IScanValidateModel, IScanValidateData>('scan-validate', { context }).pipe(
      takeOnce(),
      catchError((error: IRemoteError) => {
        const { data } = error?.response?.result;
        this.#scanDocumentStore.dispatchSaveScanValidateData({ data });
        this.#scanDocumentStore.dispatchScanValidateFailure({ error });
        return this.#scanSeverities.inspect(error);
      }),
      filterStatus()
    );
  }

  saveScannedDocuments(): Observable<IHttpStatus> {
    return this.#httpService.post<IScanDocumentModel[], IScanDocumentData>('save-scanned-documents').pipe(
      takeOnce(),
      tapResult((data: IScanDocumentData) => {
        this.#scanDocumentStore.dispatchSubmitScanDocumentSuccess({ data });
      }),
      filterStatus(),
      catchError((error: IRemoteError) => {
        this.#scanDocumentStore.dispatchSubmitScanDocumentFailure({ error });
        // In case of error we don't want to break the flow so we return of null.
        return of(null);
      })
    );
  }

  navigateToSelectedDocument(selectedDocId: number) {
    this.#navAdapter.toScanDocument(selectedDocId);
  }
}
